// src/api/myAssignments.js

import api from './api';
import { getUserEnrolledProgramsAuth } from './enrolledPrograms';

/**
 * Fetch assigned team items for a list of programs
 * Returns only items assigned to the current user and excludes "Done" items
 */
export const getAssignedItemsForPrograms = async (programs, userProfile) => {
  const allAssignedItems = [];

  for (const program of programs) {
    try {
      const data = await api.get(`/api/team-items?programId=${program._id}`);
      const teamItems = data.teamItems || [];
      
      if (!Array.isArray(teamItems)) {
        continue;
      }

      // Filter for items assigned to current user and exclude "Done" items
      const assignedItems = teamItems
        .filter(item => {
          const isAssignedToUser = item.assignedTo && (
            item.assignedTo._id === userProfile._id || 
            item.assignedTo === userProfile._id
          );
          const isNotDone = item.status !== 'Done';
          
          return isAssignedToUser && isNotDone;
        })
        .map(item => ({
          ...item,
          programId: program._id,
          programName: program.name,
          clubId: program.clubId,
          clubName: program.clubName
        }));

      allAssignedItems.push(...assignedItems);
    } catch (err) {
      // Continue on error
    }
  }

  // Sort by due date (items with due dates first, then by priority)
  allAssignedItems.sort((a, b) => {
    // Items with due dates come first
    if (a.dueDate && !b.dueDate) return -1;
    if (!a.dueDate && b.dueDate) return 1;
    
    // If both have due dates, sort by date
    if (a.dueDate && b.dueDate) {
      const dateA = new Date(a.dueDate);
      const dateB = new Date(b.dueDate);
      const dateDiff = dateA - dateB;
      if (dateDiff !== 0) return dateDiff;
    }
    
    // If dates are equal or both don't have dates, sort by priority
    const priorityOrder = { 'High': 1, 'Medium': 2, 'Low': 3 };
    const priorityA = priorityOrder[a.priority] || 4;
    const priorityB = priorityOrder[b.priority] || 4;
    return priorityA - priorityB;
  });

  return allAssignedItems;
};

/**
 * Get all assigned items across all enrolled programs
 */
export const getUserAssignments = async (userClubs, userProfile, limit = 10) => {
  try {
    const enrolledPrograms = await getUserEnrolledProgramsAuth(userClubs, userProfile);

    if (enrolledPrograms.length === 0) {
      return [];
    }

    const assignedItems = await getAssignedItemsForPrograms(enrolledPrograms, userProfile);

    return assignedItems.slice(0, limit);
  } catch (err) {
    return [];
  }
};
